import { z } from 'zod';

// The in-app path a visitor returns to once they're signed in — the `?next=`
// search param. The signed-out bounce (`redirectToSignIn`) sets it on
// /sign-in, the sign-in form forwards it to /auth/finishing, and finishing
// lands the member there. It arrives from the URL, so it's untrusted: an
// unchecked value is an open redirect.

// Resolving against a sentinel origin and requiring the result to stay on it
// rejects absolute URLs and protocol-relative `//host`, AND the inputs the URL
// parser normalises into `//host` (`/\host`, `/<tab>/host`) — which a plain
// `startsWith('/') && !startsWith('//')` check lets through.
const SENTINEL_ORIGIN = 'https://in-app.invalid';

export function parseReturnPath(raw: string): string | null {
  if (!raw.startsWith('/')) return null;
  let url: URL;
  try {
    url = new URL(raw, SENTINEL_ORIGIN);
  } catch {
    // An unparseable authority (`//[`) is no in-app path. Rejected here
    // rather than thrown: a throw escapes the search schema's `.catch` and
    // fails the whole route.
    return null;
  }
  if (url.origin !== SENTINEL_ORIGIN) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

// `path` carrying the return path as `?next=`, for URLs handed to Clerk (the
// OAuth redirect targets), which take a string rather than router search.
export function withReturnPath(path: string, next: string | undefined): string {
  return next === undefined ? path : `${path}?${new URLSearchParams({ next }).toString()}`;
}

// Search-schema field for `next`. An unsafe or malformed value is dropped
// (the visitor lands on the default destination) rather than failing the
// route's whole search validation.
export const returnPathSearchParam = z
  .string()
  .transform((raw) => parseReturnPath(raw) ?? undefined)
  .optional()
  .catch(undefined);
